import {
  ChoiceList,
  IssueList,
  Labelled,
  NumberInput,
  Worked,
  smallButtonClass,
} from '../points/controls';
import { roundSalary, type SalaryBand, type SalaryRounding } from './salaryDefinition';
import { salaryHasError, salaryIssuesFor, type SalaryValidation } from './salaryValidate';
import { FLOOR_BAND_NOTE, ROUNDING_CHOICES, formatSalary } from './salaryWording';

/**
 * §4.2's `percentileBands` — what each slice of the pool costs.
 *
 * <b>The edges are edited as shares of the pool, never as percentiles.</b> §9.3 forbids showing a
 * raw percentile and it is right to: a percentile rank here is a weighted sum of other percentile
 * ranks, which is not a quantity a card room manager has any intuition about. What they do have an
 * intuition about is "the weakest half of the pool costs 3, the strongest 2% cost 45" — so each
 * row carries a **share**, and the edges are derived cumulatively from those shares.
 *
 * That framing is not only kinder, it is stricter: the bands tile 0–100 by construction, so §4.2's
 * coverage rule cannot be broken from this editor at all. The gap and overlap checks stay in the
 * validator because a document can arrive from elsewhere — a clone, a hand-written definition, a
 * later schema — and the screen has to report what is stored rather than what it would have
 * written.
 *
 * <b>The last row's share is the remainder and is not editable.</b> Something has to absorb the
 * rounding, and making it the top band is the only choice that keeps the ladder covering the whole
 * pool after every keystroke.
 */
export function SalaryBandsEditor({
  bands,
  onChange,
  rounding,
  onRoundingChange,
  validation,
  readOnly = false,
}: {
  bands: readonly SalaryBand[];
  onChange: (next: SalaryBand[]) => void;
  rounding: SalaryRounding;
  onRoundingChange: (next: SalaryRounding) => void;
  validation: SalaryValidation;
  readOnly?: boolean;
}) {
  const shares = bands.map((band) =>
    band.fromPercentile === null || band.toPercentile === null
      ? null
      : band.toPercentile - band.fromPercentile,
  );

  /** Re-lays every edge from the shares, so the ladder always runs 0 → 100 with no gap. */
  const relay = (widths: readonly (number | null)[], prices: readonly (number | null)[]) => {
    const next: SalaryBand[] = [];
    let cursor = 0;

    widths.forEach((width, i) => {
      const last = i === widths.length - 1;
      const span = last ? Math.max(100 - cursor, 0) : Math.max(width ?? 0, 0);
      const to = last ? 100 : Math.min(cursor + span, 100);

      next.push({
        fromPercentile: round4(cursor),
        toPercentile: round4(to),
        salary: prices[i] ?? null,
      });

      cursor = to;
    });

    onChange(next);
  };

  const setShare = (index: number, share: number | null) =>
    relay(
      shares.map((s, i) => (i === index ? share : s)),
      bands.map((b) => b.salary),
    );

  const setPrice = (index: number, salary: number | null) =>
    onChange(bands.map((band, i) => (i === index ? { ...band, salary } : band)));

  const used = shares
    .slice(0, -1)
    .reduce((sum: number, share) => sum + Math.max(share ?? 0, 0), 0);
  const remainder = round4(100 - used);

  return (
    <div className="space-y-4">
      <SalaryLadder bands={bands} rounding={rounding} />

      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="text-[10px] uppercase tracking-[0.08em] text-ink-800/45">
            <tr className="border-b border-line">
              <th className="py-2 pr-3 font-semibold">Share of the pool</th>
              <th className="px-3 py-2 font-semibold">Who that is</th>
              <th className="px-3 py-2 text-right font-semibold">Costs</th>
              <th className="py-2 pl-3" />
            </tr>
          </thead>
          <tbody>
            {bands.map((band, index) => {
              const last = index === bands.length - 1;
              const invalid = salaryHasError(validation, 'bands', index);
              const priced =
                band.salary === null ? null : roundSalary(band.salary, rounding);

              return (
                <tr key={index} className="border-b border-line/60 last:border-b-0">
                  <td className="py-2 pr-3">
                    {last ? (
                      <span
                        title="Whatever is left. The top band takes the remainder so the ladder always covers everybody."
                        className="num inline-block w-20 py-1.5 text-right text-sm text-ink-800/60"
                      >
                        {remainder > 0 ? `${trim(remainder)}%` : '—'}
                      </span>
                    ) : (
                      <span className="flex items-baseline gap-1">
                        <NumberInput
                          value={shares[index] ?? null}
                          onChange={(share) => setShare(index, share)}
                          ariaLabel={`Share of the pool in band ${index + 1}`}
                          min={0}
                          step={1}
                          disabled={readOnly}
                          invalid={invalid}
                          className="w-20 text-right"
                        />
                        <span className="text-xs text-ink-800/45">%</span>
                      </span>
                    )}
                  </td>

                  <td className="px-3 py-2 text-ink-800/70">
                    {index === 0
                      ? 'The weakest, hardest to tell apart'
                      : last
                        ? 'The players a roster is built around'
                        : 'In between'}
                  </td>

                  <td className="px-3 py-2 text-right">
                    <span className="inline-flex items-baseline gap-1.5">
                      <NumberInput
                        value={band.salary}
                        onChange={(salary) => setPrice(index, salary)}
                        ariaLabel={`Price for band ${index + 1}`}
                        min={0}
                        step={1}
                        disabled={readOnly}
                        invalid={invalid}
                        className="w-20 text-right"
                      />
                      {/* Shown only when rounding actually moves it — an operator who typed 8 does
                          not need to be told it will be charged at 8. */}
                      {priced !== null && band.salary !== null && priced !== band.salary ? (
                        <span
                          className="num text-[11px] text-warn"
                          title="What this band will actually charge once rounded."
                        >
                          → {formatSalary(priced)}
                        </span>
                      ) : null}
                    </span>
                  </td>

                  <td className="py-2 pl-3 text-right">
                    {!readOnly && bands.length > 1 && !last ? (
                      <button
                        type="button"
                        onClick={() =>
                          relay(
                            shares.filter((_, i) => i !== index),
                            bands.filter((_, i) => i !== index).map((b) => b.salary),
                          )
                        }
                        className={smallButtonClass}
                      >
                        Remove
                      </button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {!readOnly ? (
        <button
          type="button"
          disabled={bands.length >= 20 || remainder <= 1}
          title={
            remainder <= 1
              ? 'The bands already cover the whole pool. Make one of the rows above narrower first.'
              : undefined
          }
          onClick={() => {
            // The new band takes half of what the top band currently holds, and the top band keeps
            // the rest — so adding one never leaves the pool uncovered, even for a keystroke.
            const width = Math.max(round4(remainder / 2), 1);
            const top = bands[bands.length - 1];

            relay(
              [...shares.slice(0, -1), width, null],
              [...bands.slice(0, -1).map((b) => b.salary), top?.salary ?? null, top?.salary ?? null],
            );
          }}
          className={smallButtonClass}
        >
          Add a band
        </button>
      ) : null}

      <Worked>{FLOOR_BAND_NOTE}</Worked>

      <IssueList issues={salaryIssuesFor(validation, 'bands')} />

      <div className="border-t border-line pt-4">
        <Labelled
          label="Round every price"
          hint="Salaries are budget units, not dollars — whole numbers keep the arithmetic something an entrant can do in their head."
        >
          <div className="mt-1 flex flex-wrap items-end gap-4">
            <div className="min-w-56 flex-1">
              <ChoiceList
                name="salary-rounding"
                options={ROUNDING_CHOICES}
                value={rounding.mode}
                disabled={readOnly}
                onChange={(mode) => onRoundingChange({ ...rounding, mode })}
              />
            </div>
            <Labelled label="To the nearest" className="w-28 shrink-0">
              <NumberInput
                value={rounding.increment}
                onChange={(increment) => onRoundingChange({ ...rounding, increment })}
                ariaLabel="Round prices to the nearest"
                min={0}
                step={1}
                disabled={readOnly}
                className="w-full text-right"
              />
            </Labelled>
          </div>
        </Labelled>
      </div>
    </div>
  );
}

/**
 * The ladder as a picture: width is how much of the pool a band holds, height is what it costs.
 *
 * A table of shares and prices is precise and says nothing about *shape* — whether the floor
 * swallows the pool, whether the top is a cliff or a slope. That is the thing an operator is
 * actually deciding, and it reads off two dimensions instantly. Same idiom, and the same reason,
 * as the points designer's placement chart.
 */
function SalaryLadder({
  bands,
  rounding,
}: {
  bands: readonly SalaryBand[];
  rounding: SalaryRounding;
}) {
  const priced = bands.map((band) =>
    band.salary === null ? null : roundSalary(band.salary, rounding),
  );
  const peak = Math.max(...priced.map((p) => p ?? 0), 0);
  if (peak <= 0) return null;

  return (
    <figure className="min-w-0">
      <div
        role="img"
        aria-label={describe(bands, priced)}
        className="flex h-24 items-end gap-px border-b border-line"
      >
        {bands.map((band, i) => {
          const width =
            band.fromPercentile === null || band.toPercentile === null
              ? 0
              : band.toPercentile - band.fromPercentile;
          const price = priced[i] ?? 0;

          return (
            <span
              key={i}
              title={`${trim(width)}% of the pool at ${formatSalary(price)}`}
              className="min-w-0 bg-felt-700"
              style={{
                width: `${Math.max(width, 0)}%`,
                height: `${Math.max((price / peak) * 100, price > 0 ? 2 : 0)}%`,
              }}
            />
          );
        })}
      </div>
      <figcaption className="mt-1.5 flex flex-wrap justify-between gap-x-4 text-[11px] text-ink-800/50">
        <span>Width is how much of the pool, height is what it costs</span>
        <span className="num">{formatSalary(peak)} at the top</span>
      </figcaption>
    </figure>
  );
}

/** The chart in words, for a screen reader. Reads the ladder, not the bars. */
function describe(bands: readonly SalaryBand[], priced: readonly (number | null)[]): string {
  const parts = bands.map((band, i) => {
    const width =
      band.fromPercentile === null || band.toPercentile === null
        ? '?'
        : trim(band.toPercentile - band.fromPercentile);
    return `${width}% at ${priced[i] === null ? 'no price' : formatSalary(priced[i]!)}`;
  });

  return `Price ladder, weakest first: ${parts.join(', ')}.`;
}

function round4(value: number): number {
  return Number(value.toFixed(4));
}

function trim(value: number): string {
  return Number(value.toFixed(2)).toString();
}
